import { create } from 'zustand';
import type { LobbyTableInfo } from '@draughts/shared';
import { log } from '../utils/logger';

/**
 * The public table list and the connected-people counters.
 *
 * Pure state, like online-store: the emits live in `services/socket.ts` and the
 * incoming callbacks in `multiplayer-store`, which is where every socket
 * callback in this app is wired.
 *
 * `myTableId` is what makes the "sei in coda" banner possible: a table outlives
 * the overlay that opened it, so the app has to remember it is waiting even
 * when nothing lobby-shaped is on screen.
 */
interface LobbyStore {
  tables: LobbyTableInfo[];
  /** Our own open table, or null when we are not queued. */
  myTableId: string | null;
  /** When we opened it — the banner counts up from here. */
  myTableSince: number | null;
  /** True while a quick/create/sit request is in flight, to disarm the buttons. */
  isBusy: boolean;
  /** i18n key of the last rejection, shown in the overlay. */
  error: string | null;

  setSnapshot: (tables: LobbyTableInfo[], myTableId: string | null) => void;
  addTable: (table: LobbyTableInfo) => void;
  removeTable: (tableId: string) => void;
  setMyTable: (tableId: string | null) => void;
  setBusy: (busy: boolean) => void;
  setError: (error: string | null) => void;
  /**
   * La presenza e' scaduta: queste righe non descrivono piu' niente di vivo.
   *
   * Non e' `reset()`, e le differenze sono tutte volute — vedi l'attuazione.
   */
  invalida: () => void;
  reset: () => void;
}

/**
 * I tavoli ritirati di recente, per id.
 *
 * Serve contro una corsa vera: `createQuickTable` chiama `setMyTable(tableId)`
 * DOPO l'ack di `lobby:quick`, ma il server pubblica il tavolo (e puo' quindi
 * gia' averlo consumato) prima di rispondere. Un ospite — o un bot della flotta
 * — che si siede dentro quel giro di rete fa arrivare `lobby:table-removed`
 * mentre `myTableId` e' ancora `null`: la rimozione non trova niente, e l'ack
 * subito dopo installa un tavolo GIA' MORTO. Da li' in poi il banner «Sei in
 * coda» non se ne va piu', perche' l'unico evento che lo spegneva e' passato.
 *
 * La finestra e' la stessa con cui il server dimentica un tavolo ritirato
 * (`lobbyService.forget`, 60 s): oltre quella nessuno puo' piu' parlarne.
 */
const MEMORIA_RITIRATI_MS = 60_000;
const ritirati = new Map<string, number>();

function ricordaRitirato(tableId: string): void {
  const ora = Date.now();
  ritirati.set(tableId, ora);
  // Potatura opportunistica: la mappa vive quanto la scheda, e senza questo
  // crescerebbe di una voce per ogni tavolo visto passare in lobby.
  for (const [id, quando] of ritirati) {
    if (ora - quando > MEMORIA_RITIRATI_MS) ritirati.delete(id);
  }
}

function ritiratoDiRecente(tableId: string): boolean {
  const quando = ritirati.get(tableId);
  if (quando === undefined) return false;
  if (Date.now() - quando > MEMORIA_RITIRATI_MS) {
    ritirati.delete(tableId);
    return false;
  }
  return true;
}

export const useLobbyStore = create<LobbyStore>((set, get) => ({
  tables: [],
  myTableId: null,
  myTableSince: null,
  isBusy: false,
  error: null,

  setSnapshot: (tables, myTableId) => {
    const previous = get().myTableId;
    set({
      tables,
      myTableId,
      // Keep the original timestamp when the server confirms the table we
      // already knew about, so a reconnect does not restart the wait counter.
      myTableSince: myTableId ? (previous === myTableId ? get().myTableSince : Date.now()) : null,
      error: null,
    });
  },

  addTable: (table) =>
    set((state) => {
      if (state.tables.some((t) => t.id === table.id)) return state;
      // Server order is oldest-first and that order is the queue itself, so the
      // list is append-only here: a new table is by definition the newest.
      return { tables: [...state.tables, table] };
    }),

  removeTable: (tableId) => {
    // Prima di toccare lo stato: da qui in poi quell'id non e' piu' installabile
    // come «il mio tavolo», nemmeno da un ack arrivato in ritardo.
    ricordaRitirato(tableId);
    set((state) => ({
      tables: state.tables.filter((t) => t.id !== tableId),
      ...(state.myTableId === tableId ? { myTableId: null, myTableSince: null } : {}),
    }));
  },

  setMyTable: (tableId) => {
    if (tableId && ritiratoDiRecente(tableId)) {
      // L'ack e' arrivato dopo il ritiro del suo stesso tavolo: installarlo
      // vorrebbe dire mettersi in coda a un posto che non esiste piu'.
      log.info('Lobby', 'setMyTable ignorato: tavolo gia\' ritirato', { tableId });
      return;
    }
    set((state) => ({
      myTableId: tableId,
      myTableSince: tableId ? (state.myTableId === tableId ? state.myTableSince : Date.now()) : null,
    }));
  },

  setBusy: (isBusy) => set({ isBusy }),

  setError: (error) => {
    if (error) log.info('Lobby', 'error', { error });
    set({ error });
  },

  /**
   * Svuotamento CHIRURGICO, da non confondere con `reset()` qui sotto.
   *
   * Lo chiama solo `invalidaPresenza` nel multiplayer-store, quando il socket e'
   * giu' da abbastanza (o quando il dispositivo dichiara di non avere rete): da
   * quel momento i tavoli sono una fotografia, non una lista.
   *
   * Tre cose NON si toccano, e ognuna ha il suo perche':
   *
   * - `isBusy`. Rimetterlo a `false` mentre una `lobby:quick` e' ancora in volo
   *   riarmerebbe i bottoni a meta' richiesta, cioe' un doppio invio. Non
   *   serve: a presenza scaduta i bottoni li disarma `presenzaScaduta`, e il
   *   `finally` della richiesta lo spegne da se' — l'ack ha un tetto
   *   (`conTetto`), quindi non resta appeso.
   * - `error`. Se si e' appena letto un rifiuto, non glielo si strappa via.
   * - la mappa `ritirati`. `reset()` la pulisce, questa no: e' la guardia contro
   *   un ack in ritardo che installa un tavolo GIA' morto (vedi il commento in
   *   testa al file), e una disconnessione e' esattamente il momento in cui
   *   quella corsa e' piu' probabile.
   *
   * `myTableId` e `myTableSince` invece vanno via insieme ai tavoli, e non per
   * simmetria: `QueuedTableBanner` ricava variante e cadenza da `tables`, quindi
   * azzerare solo quelli lo lascerebbe a schermo senza piu' sapere cosa sta
   * aspettando. E lato server quel tavolo non esiste piu' comunque — l'host lo
   * perde all'istante, senza grazia.
   */
  invalida: () => set({ tables: [], myTableId: null, myTableSince: null }),

  reset: () => {
    ritirati.clear();
    set({ tables: [], myTableId: null, myTableSince: null, isBusy: false, error: null });
  },
}));
